import { FormikProvider, getIn, useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom'
import { Container, Form, TabContent, TabPane } from 'reactstrap'
import * as Yup from 'yup'
import { handleImagesPayload, handleShouldImagesUpdate } from '../../api/images/helpers'
import images from '../../api/images/hooks'
import properties from '../../api/properties/hooks'
import { ENTITY_TYPE } from '../../common/constants'
import { propertyTransactionsFields, realEstateGeneralFields } from '../../common/forms/generalRealEstate/fields'
import { propertyResponseFields } from '../../common/response/property'
import AppPermissionsSwitcher from '../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../Components/AppPermissionsSwitcher/constants/actions'
import FormActionButtons from '../../Components/Common/FormActionButtons'
import Loader from '../../Components/Common/Loader'
import PageTitle from '../../Components/Common/PageTitle'
import { DocumentFormTab } from '../../Components/Documents'
import { useMapFormConfig, useRealEstateMaterials3DVideoFormConfig } from '../../Components/Hooks/FormHooks'
import ImagesFormTab from '../../Components/ImagesFormTab/ImagesFormTab'
import MapFormTab, { MapFormTabMain, MapFormTabSide } from '../../Components/MapFormTab/MapFormTab'
import { useFormValidationErrorModal } from '../../Components/Modals/FormValidationErrorModal/useFormValidationErrorModal'
import PropertyFormDescriptionTab, {
    PropertyFormDescriptionTabMain,
    PropertyFormDescriptionTabSide,
} from '../../Components/PropertyFormDescriptionTab/PropertyFormDescriptionTab'
import PropertyFormExportTab, {
    PropertyFormExportTabMain,
    PropertyFormExportTabSide,
} from '../../Components/PropertyFormExportTab/PropertyFormExportTab'
import PropertyFormGeneralTab, {
    PropertyFormGeneralTabMain,
    PropertyFormGeneralTabSide,
} from '../../Components/PropertyFormGeneralTab/PropertyFormGeneralTab'
import PropertyFormMaterialsTab from '../../Components/PropertyFormMaterialsTab/PropertyFormMaterialsTab'
import PropertyFormTransactionsTab, {
    PropertyFormTransactionsTabMain,
    PropertyFormTransactionsTabSide,
} from '../../Components/PropertyFormTransactionsTab/PropertyFormTransactionsTab'
import VirtualWalkVideoTab from '../../Components/VirtualWalkVideoTab/VirtualWalkVideoTab'
import { useInitializeLocationFilters } from '../../hooks/useInitializeLocationFilters'
import { resetFormFilters } from '../../store/formFilters'
import { changeTranslationObject, findEntityTitle, normalizeDecimalFields } from '../../utils/general'
import { numeralInputFields, propertyGeneralFormTabs } from './constants'
import { usePropertyDescriptionFormConfig, usePropertyExportFormConfig, usePropertyGeneralFormConfig, usePropertyTransactionsFormConfig } from './hooks'

const PropertyEdit = () => {
    const history = useHistory()
    const location = useLocation()
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const { id } = useParams()
    const module = 'property'

    // activeTab: documents (PropertyView action) ?? activeTab: general
    const [activeTab, setActiveTab] = useState(location?.state?.activeTab ?? 'general')
    const [activeMaterialsTab, setActiveMaterialsTab] = useState(null)

    const { isLoading: fetchingProperty, data: propertyData } = properties.GetProperty.useQuery(id)
    const { mutate: updateProperty } = properties.CreateUpdateProperty.useMutation()

    const { isLoading: fetchingPhotos, data: photosData } = images.GetImages.useQuery({ module, parent: id, collection: 'photo' })
    const { isLoading: fetchingGroundPlans, data: groundPlansData } = images.GetImages.useQuery({ module, parent: id, collection: 'ground_plan' })
    const { mutate: updateImages } = images.UpdateImages.useMutation()

    const property = propertyData?.item ?? null
    const photos = photosData ?? []
    const groundPlans = groundPlansData ?? []

    // used for images
    const entityData = useMemo(
        () => ({
            parent: id,
            parentInternalAppId: property?.internalAppId,
            city: property?.city,
            widerArea: property?.widerArea,
        }),
        [id, property?.internalAppId, property?.city, property?.widerArea]
    )

    useInitializeLocationFilters(property)
    const { formInputs, formikConfig } = usePropertyGeneralFormConfig(true, property)
    const { descriptionInputs, descriptionValidationConfig } = usePropertyDescriptionFormConfig(property?.translations)
    const { mapInputs, mapValidationConfig } = useMapFormConfig(property)
    const { exportValidationConfig } = usePropertyExportFormConfig(property)
    const { materials3DVideoInputs, materials3DVideoValidationConfig } = useRealEstateMaterials3DVideoFormConfig(property)
    const { transactionsInputs, transactionsValidationConfig } = usePropertyTransactionsFormConfig(property)

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            general: {
                ...formikConfig.initialValues,
                ...normalizeDecimalFields(formikConfig.initialValues, numeralInputFields),
            },
            description: descriptionValidationConfig.initialValues ?? {},
            map: mapValidationConfig.initialValues ?? {},
            transactions: transactionsValidationConfig.initialValues ?? {},
            virtualWalkVideo: materials3DVideoValidationConfig.initialValues ?? {},
            export: exportValidationConfig.initialValues ?? {},
        },
        validationSchema: Yup.object({
            general: formikConfig.validationSchema ?? Yup.object({}),
            description: descriptionValidationConfig.validationSchema ?? Yup.object({}),
            map: mapValidationConfig.validationSchema ?? Yup.object({}),
            transactions: transactionsValidationConfig.validationSchema ?? Yup.object({}),
            virtualWalkVideo: materials3DVideoValidationConfig.validationSchema ?? Yup.object({}),
            export: exportValidationConfig.validationSchema ?? Yup.object({}),
        }),
        onSubmit: (values, { setSubmitting }) => {
            const generalValues = formikConfig.prepareModelForSubmit(values.general)
            const transactionValues = transactionsValidationConfig.prepareModelForSubmit(values.transactions)

            const translations = changeTranslationObject(values.description, 'property_title', 'property_description')

            delete transactionValues[propertyTransactionsFields.PROPERTY_STATUS_ID]

            if (activeTab === 'transactions') generalValues.owner_id = values.transactions.owner_collection

            const submitValues = {
                ...generalValues,
                ...values.map,
                ...values.virtualWalkVideo,
                ...values.export,
                propertyPortalCustomValue: JSON.stringify(values.export.propertyPortalCustomValue),
                translations,
                property_status_id: values.transactions[propertyTransactionsFields.PROPERTY_STATUS_ID],
                [realEstateGeneralFields.PRICE]: values?.general[realEstateGeneralFields.PRICE]?.toString().replace(/\./g, '').replace(/,/g, '.'),
                [realEstateGeneralFields.PRICE_INITIAL]: values?.general[realEstateGeneralFields.PRICE_INITIAL]
                    ?.toString()
                    .replace(/\./g, '')
                    .replace(/,/g, '.'),
                [realEstateGeneralFields.PRICE_CLIENT]: values?.general[realEstateGeneralFields.PRICE_CLIENT]
                    ?.toString()
                    .replace(/\./g, '')
                    .replace(/,/g, '.'),
                [realEstateGeneralFields.PRICE_M2]: values?.general[realEstateGeneralFields.PRICE_M2]
                    ?.toString()
                    .replace(/\./g, '')
                    .replace(/,/g, '.'),
                [realEstateGeneralFields.PRICE_OLD]: values?.general[realEstateGeneralFields.PRICE_OLD]
                    ?.toString()
                    .replace(/\./g, '')
                    .replace(/,/g, '.'),
                property_transaction_entity: transactionValues,
            }

            const images = handleImagesPayload(module, id)
            const shouldUpdateImages = handleShouldImagesUpdate(images)
            const shouldUpdateProperty = formik.dirty || !shouldUpdateImages

            if (shouldUpdateProperty) {
                updateProperty({ data: submitValues, isEdit: true, setFieldValue: formik.setFieldValue })
            }

            if (shouldUpdateImages) {
                updateImages(images)
            }

            setSubmitting(false)
        },
    })

    const propertyServiceId = getIn(formik.values?.general, realEstateGeneralFields.PROPERTY_SERVICE_ID)
    const propertyTypeId = getIn(formik.values?.general, realEstateGeneralFields.PROPERTY_TYPE_ID)
    const hasServiceAndType = Boolean(propertyServiceId && propertyTypeId)

    // tabs definition
    const tabs = propertyGeneralFormTabs(true, hasServiceAndType)
    const materialsChildTabs = tabs.find((x) => x.name === 'materials')?.children ?? []

    useEffect(() => {
        if (location.state?.activeTab) {
            setActiveTab(location.state.activeTab)
        }
    }, [location.key, location.state?.activeTab])

    useEffect(() => {
        return () => {
            dispatch(resetFormFilters())
        }
    }, [dispatch])

    useFormValidationErrorModal(formik, [formInputs, transactionsInputs, descriptionInputs, mapInputs, materials3DVideoInputs])

    const handleTabChange = (tab) => {
        if (activeTab === tab) return

        if (tab === 'materials') {
            const photosTab = materialsChildTabs[0].name
            setActiveMaterialsTab(photosTab)
        }

        setActiveTab(tab)
    }

    const handleMaterialsTabChange = (tab) => {
        if (activeMaterialsTab !== tab) {
            setActiveMaterialsTab(tab)
        }
    }

    const renderFormButtons = () => <FormActionButtons disabled={false} hasCancel={true} cancelAction={() => history.push(`/properties/${id}`)} />

    if (fetchingProperty) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Loader />
                </Container>
            </div>
        )
    }

    return (
        <div className="page-content">
            <AppPermissionsSwitcher permission="ModulePermissions" module={location} action={action.edit}>
                {({ hasModulePermission }) => hasModulePermission === false && <Redirect to="/properties" />}
            </AppPermissionsSwitcher>
            <Container fluid>
                <FormikProvider value={formik}>
                    <Form onSubmit={(e) => formik.handleSubmit(e)}>
                        <PageTitle title={`${t('page.edit')} ${findEntityTitle(property, propertyResponseFields.PROPERTY_TITLE)}`}>
                            {renderFormButtons()}
                        </PageTitle>

                        <TabContent activeTab={activeTab} className="text-muted">
                            {activeTab === 'general' && (
                                <TabPane tabId="general">
                                    <PropertyFormGeneralTab
                                        subform="general"
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        handleTabChange={handleTabChange}
                                        inputs={formInputs}
                                        formik={formik}
                                        module={location}
                                        id={id}
                                        property={property}
                                    >
                                        <PropertyFormGeneralTabSide />
                                        <PropertyFormGeneralTabMain />
                                    </PropertyFormGeneralTab>
                                </TabPane>
                            )}
                            {activeTab === 'description' && (
                                <TabPane tabId="description">
                                    <PropertyFormDescriptionTab
                                        subform="description"
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        inputs={descriptionInputs}
                                        formik={formik}
                                        id={id}
                                        handleTabChange={handleTabChange}
                                    >
                                        <PropertyFormDescriptionTabSide />
                                        <PropertyFormDescriptionTabMain />
                                    </PropertyFormDescriptionTab>
                                </TabPane>
                            )}
                            {activeTab === 'map' && (
                                <TabPane tabId="map">
                                    <MapFormTab
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        inputs={mapInputs}
                                        formik={formik}
                                        entityType={ENTITY_TYPE.PROPERTY}
                                        handleTabChange={handleTabChange}
                                    >
                                        <MapFormTabSide />
                                        <MapFormTabMain />
                                    </MapFormTab>
                                </TabPane>
                            )}
                            {activeTab === 'transactions' && (
                                <TabPane tabId="transactions">
                                    <PropertyFormTransactionsTab
                                        subform="transactions"
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        inputs={transactionsInputs}
                                        formik={formik}
                                        module={location}
                                        id={id}
                                        property={property}
                                        handleTabChange={handleTabChange}
                                    >
                                        <PropertyFormTransactionsTabSide />
                                        <PropertyFormTransactionsTabMain />
                                    </PropertyFormTransactionsTab>
                                </TabPane>
                            )}
                            {activeTab === 'export' && (
                                <TabPane tabId="export">
                                    <PropertyFormExportTab
                                        subform="export"
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        formik={formik}
                                        handleTabChange={handleTabChange}
                                    >
                                        <PropertyFormExportTabSide />
                                        <PropertyFormExportTabMain />
                                    </PropertyFormExportTab>
                                </TabPane>
                            )}
                            {activeTab === 'materials' && (
                                <TabPane tabId="materials">
                                    <PropertyFormMaterialsTab>
                                        <TabContent activeTab={activeMaterialsTab} className="text-muted">
                                            {activeMaterialsTab === 'photos' && (
                                                <TabPane tabId="photos">
                                                    <ImagesFormTab
                                                        module={module}
                                                        collection={'photo'}
                                                        url={`${module}/photo`}
                                                        imageData={photos}
                                                        entityData={entityData}
                                                        isFetching={fetchingPhotos}
                                                        isSubmitting={formik.isSubmitting}
                                                        activeTab={activeTab}
                                                        activeChildTab={activeMaterialsTab}
                                                        tabs={tabs}
                                                        childTabs={materialsChildTabs}
                                                        handleTabChange={handleTabChange}
                                                        handleChildTabChange={handleMaterialsTabChange}
                                                    />
                                                </TabPane>
                                            )}
                                            {activeMaterialsTab === 'groundPlans' && (
                                                <TabPane tabId="groundPlans">
                                                    <ImagesFormTab
                                                        module={module}
                                                        collection={'ground_plan'}
                                                        url={`${module}/ground_plan`}
                                                        imageData={groundPlans}
                                                        entityData={entityData}
                                                        isFetching={fetchingGroundPlans}
                                                        isSubmitting={formik.isSubmitting}
                                                        activeTab={activeTab}
                                                        activeChildTab={activeMaterialsTab}
                                                        tabs={tabs}
                                                        childTabs={materialsChildTabs}
                                                        handleTabChange={handleTabChange}
                                                        handleChildTabChange={handleMaterialsTabChange}
                                                    />
                                                </TabPane>
                                            )}
                                            {activeMaterialsTab === '3dVideo' && (
                                                <TabPane tabId="3dVideo">
                                                    <VirtualWalkVideoTab
                                                        subform="virtualWalkVideo"
                                                        tabs={tabs}
                                                        activeTab={activeTab}
                                                        activeMaterialsTab={activeMaterialsTab}
                                                        materialsTabs={materialsChildTabs}
                                                        inputs={materials3DVideoInputs}
                                                        formik={formik}
                                                        handleTabChange={handleTabChange}
                                                        handleMaterialsTabChange={handleMaterialsTabChange}
                                                    />
                                                </TabPane>
                                            )}
                                        </TabContent>
                                    </PropertyFormMaterialsTab>
                                </TabPane>
                            )}
                        </TabContent>
                    </Form>
                </FormikProvider>

                {/* documents are saved via dropzone, not via the page's Formik instance */}
                {activeTab === 'documents' && (
                    <TabPane tabId="documents">
                        <DocumentFormTab tabs={tabs} activeTab={activeTab} handleTabChange={handleTabChange} documentModule="property" />
                    </TabPane>
                )}
            </Container>
        </div>
    )
}

export default PropertyEdit
